<script lang="ts" setup>
    import { computed, reactive, ref } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { stringifyPayload } from './utils'

    interface PayloadRow {
        key: string
        preview: string
        full?: string
    }

    const props = defineProps<{
        label: string
        value: unknown
    }>()

    const { t: $t } = useI18n({
        useScope: 'global',
    })

    // A preview stays on a single line: long enough to recognize the value,
    // short enough to keep the payload readable without expanding anything.
    const PREVIEW_LENGTH = 120

    const stringify = (value: unknown, indent = 0): string | undefined => {
        try {
            return JSON.stringify(value, null, indent)
        } catch {
            return undefined
        }
    }

    const toPreview = (value: unknown): string => {
        if (value === undefined) {
            return ''
        }
        const text =
            typeof value === 'string' ? value : (stringify(value) ?? '')
        const single = text.replace(/\s+/g, ' ').trim()
        return single.length > PREVIEW_LENGTH
            ? `${single.slice(0, PREVIEW_LENGTH)}…`
            : single
    }

    // Objects are listed by key, arrays by position: either way the payload
    // becomes a list of short lines instead of one wall of JSON.
    const entries = computed<[string, unknown][] | undefined>(() => {
        const value = props.value
        if (Array.isArray(value)) {
            return value.length
                ? value.map(
                      (item, index) =>
                          [`${index + 1}`, item] as [string, unknown],
                  )
                : undefined
        }
        if (value && typeof value === 'object') {
            const own = Object.entries(value as Record<string, unknown>)
            return own.length ? own : undefined
        }
        return undefined
    })

    const rows = computed<PayloadRow[] | undefined>(() =>
        entries.value?.map(([key, value]) => {
            const preview = toPreview(value)
            const full = typeof value === 'string' ? value : stringify(value, 2)
            return {
                key,
                preview: preview || '-',
                // Only a value the preview had to cut (long, multiline or
                // nested) is worth its own expandable block.
                full: full && full.trim() !== preview ? full : undefined,
            }
        }),
    )

    // Scalars and strings have no rows: they fall back to the plain payload.
    const text = computed(() =>
        rows.value ? undefined : stringifyPayload(props.value),
    )

    const summary = computed(() => {
        const list = rows.value
        if (!list) {
            return toPreview(props.value)
        }
        if (Array.isArray(props.value)) {
            return $t('messagePart.stepItems', list.length)
        }
        if (list.length === 1) {
            return `${list[0].key}: ${list[0].preview}`
        }
        return list.map((row) => row.key).join(' · ')
    })

    const isOpen = ref(false)
    const expandedRows = reactive(new Set<string>())
    const toggleRow = (key: string) => {
        if (expandedRows.has(key)) {
            expandedRows.delete(key)
        } else {
            expandedRows.add(key)
        }
    }
</script>

<template>
    <div v-if="rows || text" class="pk-chatbot-step-payload">
        <button
            type="button"
            class="pk-chatbot-step-payload__header"
            :aria-expanded="isOpen"
            @click="isOpen = !isOpen">
            <span class="pk-chatbot-step-payload__label">{{ label }}</span>
            <span class="pk-chatbot-step-payload__summary">
                {{ summary }}
            </span>
            <VvIcon
                :name="isOpen ? 'ri:arrow-up-s-line' : 'ri:arrow-down-s-line'"
                class="shrink-0" />
        </button>
        <ul v-if="isOpen && rows" class="pk-chatbot-step-payload__rows">
            <li v-for="row in rows" :key="row.key">
                <component
                    :is="row.full ? 'button' : 'div'"
                    :type="row.full ? 'button' : undefined"
                    class="pk-chatbot-step-payload__row"
                    :aria-expanded="
                        row.full ? expandedRows.has(row.key) : undefined
                    "
                    @click="row.full ? toggleRow(row.key) : undefined">
                    <span class="pk-chatbot-step-payload__key">
                        {{ row.key }}
                    </span>
                    <span class="pk-chatbot-step-payload__value">
                        {{ row.preview }}
                    </span>
                    <VvIcon
                        v-if="row.full"
                        :name="
                            expandedRows.has(row.key)
                                ? 'ri:arrow-up-s-line'
                                : 'ri:arrow-down-s-line'
                        "
                        class="shrink-0" />
                </component>
                <pre
                    v-if="row.full && expandedRows.has(row.key)"
                    class="pk-chatbot-step-payload__full"
                    >{{ row.full }}</pre>
            </li>
        </ul>
        <pre v-else-if="isOpen" class="pk-chatbot-step-payload__full">{{
            text
        }}</pre>
    </div>
</template>

<style lang="scss">
    .pk-chatbot-step-payload {
        border: var(--spacing-px) solid var(--color-surface-4);
        border-radius: var(--rounded);
        background-color: var(--color-surface-1);
        overflow: hidden;

        &__header {
            display: flex;
            align-items: center;
            gap: var(--spacing-8);
            width: 100%;
            min-height: var(--spacing-28);
            padding: var(--spacing-4) var(--spacing-8);
            text-align: left;
            cursor: pointer;
            transition: background-color 0.2s ease;

            &:hover {
                background-color: var(--color-surface-2);
            }
        }

        &__label {
            flex-shrink: 0;
            font-weight: 600;
            font-size: var(--text-10);
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: var(--color-word-3);
        }

        &__summary {
            flex: 1 1 0;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-family: var(--font-mono);
            font-size: var(--text-10);
            color: var(--color-word-4);
        }

        &__rows > li {
            border-top: var(--spacing-px) solid var(--color-surface-3);
        }

        &__row {
            display: flex;
            align-items: baseline;
            gap: var(--spacing-8);
            width: 100%;
            padding: var(--spacing-4) var(--spacing-8);
            text-align: left;
            font-size: var(--text-10);

            &:is(button) {
                cursor: pointer;
                transition: background-color 0.2s ease;

                &:hover {
                    background-color: var(--color-surface-2);
                }
            }
        }

        &__key {
            flex-shrink: 0;
            max-width: 40%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-family: var(--font-mono);
            color: var(--color-word-4);
        }

        &__value {
            flex: 1 1 0;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: var(--color-word-2);
        }

        &__full {
            padding: var(--spacing-8);
            border-top: var(--spacing-px) solid var(--color-surface-3);
            max-height: var(--spacing-160);
            overflow: auto;
            white-space: pre-wrap;
            word-break: break-word;
            font-family: var(--font-mono);
            font-size: var(--text-10);
            color: var(--color-word-3);
        }
    }
</style>
